import { useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../shared/hooks/useApi";
import { PdfFormTemplateId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (templateId: PdfFormTemplateId) => void;
};

export default function UploadPdfTemplateModal({ isOpen, onClose, onCreated }: Props) {
  const { mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");

  const createMutation = useMutation({
    ...mutations.pdfFormTemplate.create(),
    onError: (error) => {
      toast({ description: `Upload failed: ${formatErrorResponse(error)}`, status: "error" });
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ["pdf-form-templates"] });
      setFile(null);
      setName("");
      onCreated(id);
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setFile(selected);
    if (selected !== null && name.trim() === "") {
      setName(selected.name.replace(/\.pdf$/i, ""));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload PDF</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired mb={4}>
            <FormLabel>Template name</FormLabel>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>PDF file (max 20MB)</FormLabel>
            <input
              ref={fileInputRef}
              accept="application/pdf"
              type="file"
              onChange={handleFileChange}
            />
            {file !== null && (
              <Text fontSize="sm" mt={2}>
                {file.name}
              </Text>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={file === null || name.trim() === ""}
            isLoading={createMutation.isPending}
            onClick={() => {
              if (file !== null) {
                createMutation.mutate({ file, name: name.trim() });
              }
            }}
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
